import moment from "moment";
import PropTypes, { arrayOf, object } from "prop-types";
import { dataFormatters } from "../../../../utils/dataFormatters";
import React, { Fragment } from "react";
import TooltipContainer from "../../../../libraries/TooltipContainer";
import { languageService } from "../../../../Language/language.service";

/**
 * Enum for HosGraphicalView modes
 * @readonly
 * @enum {string}
 */

/**
 * @typedef {Object} HosGraphicalViewProps
 * @property {Object[]} records - HOS records
 * @property {moment.Moment | Date} viewStartDate - Day from which the graphical view displays the records
 * @property {string} timezone - Time zone in which to display the records
 * @property {Object} latestHosRecord - Last Record
 * @property {boolean} singleDayViewOnly - whether to display just the first day or multiple days
 */

/**
 *
 * @param {HosGraphicalViewProps} props
 * @returns
 */
const sampleOffDuty = {
  dutyType: "Off Duty",
  serviceType: "Off Duty",
  startMilliseconds: 0,
  endMilliseconds: 86400 * 1000,
};

/**
 * Generates an abbreviation for a given name using a lookup table and a specified format.
 * @param {Object} lookup - The lookup table containing existing abbreviations.
 * @param {string} name - The name to generate an abbreviation for.
 * @returns {string} The generated abbreviation.
 */

function getAbbreviation(lookup = {}, name) {
  function getCurrVariant(abbreviation, variant) {
    return variant > 0 ? `${abbreviation}${variant}` : abbreviation;
  }

  if (lookup[name]) return lookup[name];

  let abbreviation = name.toUpperCase().replace(" ", "").substring(0, 4);
  let variant = 0;
  do {
    // create variant with counter value if required
  } while (Object.values(lookup).find((abbr) => getCurrVariant(abbreviation, variant) === abbr) && ++variant);

  return getCurrVariant(abbreviation, variant);
}

/**
 * Renders a graphical view of the hours of service for a single day.
 * @param {Array} props.reportingPeriod - The array of records representing the reporting period.
 * @param {string} props.serviceDate - The service date string.
 * @param {string} [props.titleOverride] - An optional title override.
 * @returns {JSX.Element} The rendered graphical view component.
 */

export const SingleDayHosGraphicalView = ({ reportingPeriod = [], serviceDate, titleOverride }) => {
  if (!Array.isArray(reportingPeriod)) return null;

  const TOTAL_SECONDS_IN_DAY = 24 * 60 * 60;
  const SECONDS_IN_HOUR = 60 * 60;
  const TOTAL_HOURS_IN_DAY = 24;
  const TOTAL_BINS_PER_HOUR = 60;
  const FIRST_COL_SPAN = 400;
  const LAST_COL_SPAN = 250;
  const HOURS_ARRAY_WITH_NUMBERS = Array.from(Array(TOTAL_HOURS_IN_DAY).keys());

  const totalColSpan = FIRST_COL_SPAN + TOTAL_BINS_PER_HOUR * TOTAL_HOURS_IN_DAY + LAST_COL_SPAN;
  let _titleExt = titleOverride ? titleOverride : serviceDate;
  const title = `${languageService("Hours of Service")}${_titleExt ? ` - ${_titleExt}` : ""}`;

  // prepare data
  const hosGraphicalData = reportingPeriod.reduce(
    (result, record) => {
      const { serviceType, dutyType } = record;
      if (serviceType === "Covered Service") {
        result[serviceType].records.push({ ...record, className: "covered-service" });
      } else if (serviceType === "Commingled Service") {
        result["Covered Service"].records.push({ ...record, className: "comingled-service" });
      } else if (serviceType === "Non-covered Service") {
        result[serviceType].records.push({ ...record, className: "non-covered-service" });
      } else if (serviceType === "Off Duty") {
        result[serviceType].records.push({ ...record, className: "off-duty" });
      } else if (dutyType === "Interrupted Rest") {
        result["Non-covered Service"].records.push({ ...record, className: "interrupted-rest" });
      } else if (serviceType === "Limbo") {
        result[serviceType].records.push({ ...record, className: "limbo-service" });
      } else if (serviceType === "Meal") {
        result.Other.records.push({
          ...record,
          activityType: {
            code: "M",
            value: "",
          },
          className: "meal-offDuty",
        });
      }
      return result;
    },
    {
      "Covered Service": {
        records: [],
        hours: [],
      },
      "Non-covered Service": {
        records: [],
        hours: [],
      },
      "Off Duty": {
        records: [],
        hours: [],
        disableTooltip: true,
      },

      Limbo: {
        records: [],
        hours: [],
      },
      Other: {
        records: [],
        hours: [],
      },
    },
  );

  for (const key in hosGraphicalData) {
    let prevSeconds = 0;
    hosGraphicalData[key].totalMillieconds = hosGraphicalData[key].records
      .filter((record) => !isNaN(record.endMilliseconds) && !isNaN(record.startMilliseconds))
      .map((record) => record.endMilliseconds - record.startMilliseconds)
      .reduce((sum, millis) => millis + sum, 0);
    hosGraphicalData[key].totalTime = dataFormatters.formatTimeDuration(hosGraphicalData[key].totalMillieconds, false);
    hosGraphicalData[key].hours =
      hosGraphicalData[key].records.length > 0
        ? hosGraphicalData[key].records
            .reduce((activities, activity, index, arr) => {
              const startSeconds = activity.startMilliseconds / 1000;
              const endSeconds = activity.endMilliseconds / 1000;
              startSeconds !== 0 && activities.push({ active: false, startSeconds: prevSeconds, endSeconds: startSeconds });
              activities.push({ active: true, startSeconds: startSeconds, endSeconds: endSeconds, ...activity });
              if (index + 1 === arr.length && endSeconds < TOTAL_SECONDS_IN_DAY) {
                // last item
                activities.push({ active: false, startSeconds: endSeconds, endSeconds: TOTAL_SECONDS_IN_DAY });
              }
              prevSeconds = endSeconds;
              return activities;
            }, [])
            .reduce((resultHours, activity) => {
              const { startSeconds, endSeconds, active, startTime, endTime, startLoc, endLoc, activityType, className, serviceType } =
                activity;
              let secs = startSeconds;
              if (active) {
                resultHours.push({
                  active: true,
                  colSpan: (endSeconds - startSeconds) / (SECONDS_IN_HOUR / TOTAL_BINS_PER_HOUR), // add hour
                  startTime: startTime,
                  endTime: endTime,
                  startLoc: startLoc,
                  endLoc: endLoc,
                  activityType: activityType,
                  className: className,
                  serviceType: serviceType,
                });
              } else {
                while (secs < endSeconds && secs + SECONDS_IN_HOUR <= endSeconds) {
                  let bins = TOTAL_BINS_PER_HOUR;
                  if (secs % SECONDS_IN_HOUR !== 0) {
                    const remainder = SECONDS_IN_HOUR - (secs % SECONDS_IN_HOUR);
                    bins = (TOTAL_BINS_PER_HOUR * remainder) / SECONDS_IN_HOUR;
                    secs += remainder;
                  } else {
                    secs += (bins * SECONDS_IN_HOUR) / TOTAL_BINS_PER_HOUR;
                  }
                  resultHours.push({
                    active: false,
                    colSpan: bins, // add hour
                  });
                }
                if (secs < endSeconds) {
                  let bins = TOTAL_BINS_PER_HOUR;
                  const remainder = endSeconds - secs;

                  bins = (TOTAL_BINS_PER_HOUR * remainder) / SECONDS_IN_HOUR;
                  secs += remainder;

                  resultHours.push({
                    active: false,
                    colSpan: bins, // add hour
                  });
                }
              }

              return resultHours;
            }, [])
        : HOURS_ARRAY_WITH_NUMBERS.map((hour) => ({
            active: false,
            colSpan: TOTAL_BINS_PER_HOUR, // add hour
          }));
  }

  let abbreviationLookup = {};
  return (
    <div className={"hos-graphical-view-wrapper"}>
      <div className="no-print" style={{ flex: "auto" }}></div>
      <div className="table-bordered hos-graphical-view" style={{ flex: 8 }}>
        {/* <thead> */}
        <div className="titles border-bottom " style={{ display: "flex", height: "35px" }}>
          <div className={`tbl-bordered-header text-center bold-text cell-center`} style={{ flex: totalColSpan }}>
            {title}
          </div>
        </div>
        <div style={{ display: "flex", height: "35px" }}>
          <div
            className={`titles cell-left bold-text border-bottom tbl-bordered-header text-left pd-left-5px rect-servicetype`}
            style={{ flex: FIRST_COL_SPAN }}
          >
            {languageService("Service Type")}
          </div>
          {HOURS_ARRAY_WITH_NUMBERS.map((hour, i) => {
            return (
              <div
                key={i}
                className="border-bottom cell-center bold-text alternate-col tbl-bordered-header text-center square"
                style={{ flex: TOTAL_BINS_PER_HOUR }}
              >
                {dataFormatters.padZerosBeforeNum(hour, 2)}
              </div>
            );
          })}
          <div
            className="titles cell-center bold-text border-bottom tbl-bordered-header text-center rect-totaltime"
            style={{ flex: LAST_COL_SPAN }}
          >
            {languageService("Total Time")}
          </div>
        </div>
        {Object.keys(hosGraphicalData).map((serviceType, i) => {
          const { totalTime, hours, disableTooltip } = hosGraphicalData[serviceType];
          return (
            <div key={i} style={{ display: "flex", height: "35px" }} className="service-row">
              <div className="titles cell-left bold-text pd-left-5px rect-servicetype" style={{ flex: FIRST_COL_SPAN }}>
                {languageService(serviceType)}
              </div>
              {hours.map((hour, index) => {
                const className = hour.className ? hour.className : "";
                const startTime = hour.startTime ? hour.startTime : "-";
                const endTime = hour.endTime ? hour.endTime : "-";
                const startLoc = hour.startLoc && hour.startLoc.description ? hour.startLoc.description : "-";
                const endLoc = hour.endLoc && hour.endLoc.description ? hour.endLoc.description : "-";

                let abbreviatedStartLoc = "";
                let abbreviatedEndLoc = "";
                if (hour.active == true) {
                  abbreviatedStartLoc = getAbbreviation(abbreviationLookup, startLoc);
                  hour.startLoc && startLoc !== "-" && (abbreviationLookup[startLoc] = abbreviatedStartLoc);
                  abbreviatedEndLoc = getAbbreviation(abbreviationLookup, endLoc);
                  hour.endLoc && endLoc !== "-" && (abbreviationLookup[endLoc] = abbreviatedEndLoc);
                }
                return (
                  <div
                    key={`hour-${index}`}
                    style={{ flex: hour.colSpan, position: "relative" }}
                    className={`light-border-bottom cell-center text-center ${
                      hour.active ? `${disableTooltip ? "" : "active"} ${className}` : ""
                    }`}
                  >
                    {hour.active && !disableTooltip ? (
                      <Fragment>
                        <div>
                          {hour.endLoc ? (
                            <Fragment>
                              <div className="hos-entry-end-loc">{abbreviatedEndLoc}</div>
                              <div className="hos-entry-end-loc-line"></div>
                            </Fragment>
                          ) : null}
                          {hour.startLoc ? (
                            <Fragment>
                              <div className="hos-entry-start-loc">{abbreviatedStartLoc}</div>
                              <div className="hos-entry-start-loc-line"></div>
                            </Fragment>
                          ) : null}
                        </div>
                        <TooltipContainer
                          className="hos-graph-tooltip"
                          maxHeight
                          maxWidth
                          tooltipText={
                            <table className="hos-graph-tooltip-table">
                              <tbody>
                                {[
                                  { head: languageService("Service Type"), val: languageService(hour.serviceType) },
                                  {
                                    head: languageService("Activity Type"),
                                    val: hour.activityType && languageService(hour.activityType.value),
                                  },
                                  { head: languageService("Start Time"), val: startTime },
                                  { head: languageService("End Time"), val: endTime === "00:00" ? "24:00" : endTime },
                                  { head: languageService("Start Location"), val: startLoc },
                                  { head: languageService("End Location"), val: endLoc },
                                ].map((row) => (
                                  <tr>
                                    <td className="title text-left">{`${row.head}:`}</td>
                                    <td className="value text-left">{row.val}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          }
                        >
                          <div className={`bold-text hos-activity-area-box ${className}`}>
                            {hour.activityType && hour.activityType.code}
                          </div>
                        </TooltipContainer>
                      </Fragment>
                    ) : (
                      hour.activityType && hour.activityType.code
                    )}
                  </div>
                );
              })}
              <div className="titles cell-center bold-text text-center rect-totaltime" style={{ flex: LAST_COL_SPAN }}>
                {totalTime}
              </div>
            </div>
          );
        })}

        {/* show abbreviations and locations conditionally */}
        {Object.keys(abbreviationLookup).length > 0 && (
          <Fragment>
            <div style={{ display: "flex", height: "35px" }}></div>
            <div style={{ display: "flex", minHeight: "20px", marginTop: "22px" }}>
              <div style={{ display: "flex" }} className={`text-left`}>
                <span className="pd-left-10px bold-text">{`${languageService("LEGEND")}: `}&nbsp;&nbsp;</span>
                <span className="pd-left-10px" data-testid={"legend-value"}>
                  {Object.keys(abbreviationLookup).map((loc, index, arr) => (
                    <span>{`${abbreviationLookup[loc]}:${loc}${index < arr.length - 1 ? "," : ""}`}&nbsp;&nbsp;&nbsp;&nbsp;</span>
                  ))}
                </span>
              </div>
            </div>
          </Fragment>
        )}
      </div>
      <div className="no-print" style={{ flex: "auto" }}></div>
    </div>
  );
};

/**
 * Adds off-duty blocks to an array of records, based on the start and end times of the activities.
 * @param {Array} records - The array of records representing activities.
 * @param {boolean} submitted - Indicates if the records have been submitted.
 * @returns {Array} The array of records with off-duty blocks added.
 */
const addOffDuties = (records = [], submitted = false) => {
  if (records.length === 0) {
    return submitted ? [{ ...sampleOffDuty, startTime: "00:00", endTime: "00:00" }] : [];
  }

  return records.reduce((acc, curr, index, array) => {
    const next = array[index + 1];
    // Add an off-duty block from 00:00 to the start time of the first activity, if necessary
    if (index === 0 && curr.startMilliseconds !== 0) {
      acc.push({
        ...sampleOffDuty,
        startTime: "00:00",
        endTime: curr.startTime,
        startMilliseconds: 0,
        endMilliseconds: curr.startMilliseconds,
      });
    }
    acc.push(curr);
    if (next) {
      // Add an off-duty block from the end time of the current item to the start time of the next item, if necessary
      if (curr.endMilliseconds !== next.startMilliseconds) {
        acc.push({
          ...sampleOffDuty,
          startTime: curr.endTime,
          endTime: next.startTime,
          startMilliseconds: curr.endMilliseconds,
          endMilliseconds: next.startMilliseconds,
        });
      }
    }
    // there is no next. which means it is the last activity
    else if (submitted && curr.endMilliseconds !== 0) {
      acc.push({
        ...sampleOffDuty,
        startTime: curr.endTime,
        endTime: "00:00",
        startMilliseconds: curr.endMilliseconds,
        endMilliseconds: 86400 * 1000,
      });
    }
    return acc;
  }, []);
};

/**
 * Calculates the days between two dates and returns an array of date strings in ISO format.
 * @param {string} startDate - The start date string in a supported format.
 * @param {string} endDate - The end date string in a supported format.
 * @param {string} timezone - The timezone to use for the date calculations.
 * @returns {string[]} An array of date strings in ISO format representing the days between the start and end dates.
 */

const daysBetweenDates = (startDate, endDate, timezone) => {
  let dates = [];
  let currDate = moment.tz(startDate, timezone).startOf("day");
  let lastDate = moment.tz(endDate, timezone).startOf("day");
  while (currDate.isSameOrBefore(lastDate)) {
    dates.push(currDate.toISOString());
    currDate.add("1", "day");
  }
  return dates;
};

/**
 * Calculates the total number of milliseconds from a given date and timezone.
 * @param {Date} date - The date object.
 * @param {string} timezone - The timezone to use for the calculation.
 * @returns {number} The total number of milliseconds.
 */
const getMilliSeconds = (date, timezone) => {
  const formatDate = dataFormatters.stringifyTimeIn24HourFormatWithTimezone(date, timezone).split(":");
  const hoursInMilliSeconds = formatDate[0] * 60 * 60 * 1000;
  const minuteInMilliSeconds = formatDate[1] * 60 * 1000;
  return hoursInMilliSeconds + minuteInMilliSeconds;
};

/**
 * Splits multi-day records into multiple records if necessary, considering the provided timezone.
 * @param {Array} records - The array of records to be split. Defaults to an empty array.
 * @param {string} timezone - The timezone to use for the record splitting.
 * @returns {Array} The array of split records.
 */

const splitMultiDayRecords = (records = [], timezone) => {
  return records
    .map(({ ...record }) => {
      // format startTime, endTime. Add startMilliseconds and endMilliseconds
      let updatedRecord = {
        ...record,
        startTime: dataFormatters.stringifyTimeIn24HourFormatWithTimezone(record.startDate, timezone),
        endTime: dataFormatters.stringifyTimeIn24HourFormatWithTimezone(record.endDate, timezone),
        startMilliseconds: getMilliSeconds(record.startDate, timezone),
        endMilliseconds: getMilliSeconds(record.endDate, timezone),
      };

      let centerPointDate = moment.tz(record.endDate, timezone).startOf("day").toISOString();
      // split to multipe records if required
      let multipleRecords =
        dataFormatters.stringifyDateInYYYYMMDDWithTimezone(record.startDate, timezone) !==
        dataFormatters.stringifyDateInYYYYMMDDWithTimezone(record.endDate, timezone)
          ? [
              {
                ...updatedRecord,
                endDate: centerPointDate,
                endTime: "00:00",
                endMilliseconds: 86400 * 1000,
              },
              {
                ...updatedRecord,
                startDate: centerPointDate,
                startTime: "00:00",
                startMilliseconds: 0,
              },
            ]
          : [updatedRecord];

      return multipleRecords;
    })
    .flat();
};

/**
 * Filters the records based on the specified date within the given timezone.
 * @param {Array} records - The array of records to filter.
 * @param {moment} date - The date to filter the records for.
 * @param {string} timezone - The timezone in which the filtering should be performed.
 * @returns {Array} The filtered records that fall within the specified date.
 */
const filterRecordsInADay = (records, date, timezone) => {
  const selectedDayStart = date.clone().startOf("day");
  const selectedDayEnd = date.clone().endOf("day"); // 23:59
  const recordsForThisDay = records
    // filter records starting on or before 23:59 of the day
    .filter((record) => !moment.tz(record.startDate, timezone).isAfter(selectedDayEnd))
    // filter records ending before 00:00 of this day
    .filter((record) => !moment.tz(record.endDate, timezone).isSameOrBefore(selectedDayStart));
  return recordsForThisDay;
};

export const HosGraphicalView = ({
  records = [],
  viewStartDate,
  timezone,
  latestHosRecord,
  singleDayViewOnly,
  templateNameConvention = false,
}) => {
  // convert date to moment using timezone
  let viewStartDateMoment = moment.tz(viewStartDate, timezone);
  if (!latestHosRecord) {
    // no record has yet been added that's why latestHosRecord is empty
    // add fake latestRecordDate as start of this day
    latestHosRecord = {
      endDate: viewStartDateMoment.startOf("day"),
    };
  }
  if (singleDayViewOnly) {
    const currDateEnd = viewStartDateMoment.clone().endOf("day"); // 23:59:59 of the day
    const fullySubmitted = currDateEnd.isSameOrBefore(new Date(latestHosRecord.endDate));
    // split multi-day records
    const splittedRecords = splitMultiDayRecords(records, timezone);
    // filter records in for this day
    const recordsForThisDay = filterRecordsInADay(splittedRecords, viewStartDateMoment, timezone);
    return (
      <SingleDayHosGraphicalView
        reportingPeriod={addOffDuties(recordsForThisDay, fullySubmitted)}
        serviceDate={dataFormatters.stringifyDateInYYYYMMDDWithTimezone(viewStartDateMoment, timezone)}
      />
    );
  } else {
    const lastRecordEndDate = records.length !== 0 ? records[records.length - 1].endDate : "1970-01-01";
    const fullySubmitted = viewStartDateMoment.isSameOrBefore(new Date(lastRecordEndDate));
    if (fullySubmitted) {
      // Calculate the number of days between viewStartDate and the enddate of last records.
      const days = daysBetweenDates(viewStartDate, lastRecordEndDate, timezone);
      // Split the records into multiple records
      const splittedRecords = splitMultiDayRecords(records, timezone);
      return days.map((curr, index, arr) => {
        // Filter the records for the current day
        const recordsForThisDay = filterRecordsInADay(splittedRecords, moment.tz(curr, timezone), timezone);
        return (
          <SingleDayHosGraphicalView
            reportingPeriod={addOffDuties(recordsForThisDay, index !== arr.length - 1 ? true : false)}
            serviceDate={
              templateNameConvention
                ? `${languageService("Day")} ${index + 1}`
                : dataFormatters.stringifyDateInYYYYMMDDWithTimezone(curr, timezone)
            }
          />
        );
      });
    }
    // No need to render if records don't exist for viewStartDate and onwards
    return (
      <SingleDayHosGraphicalView
        serviceDate={
          templateNameConvention
            ? `${languageService("Day")} 1`
            : dataFormatters.stringifyDateInYYYYMMDDWithTimezone(viewStartDateMoment, timezone)
        }
      />
    );
  }
};
export { addOffDuties, filterRecordsInADay, splitMultiDayRecords, daysBetweenDates };

HosGraphicalView.propTypes = {
  records: PropTypes.arrayOf(Object).isRequired,
  viewStartDate: PropTypes.instanceOf(Date).isRequired,
  timezone: PropTypes.string.isRequired,
  singleDayViewOnly: PropTypes.bool,
  latestHosRecord: PropTypes.object,
};

HosGraphicalView.defaultProps = {
  records: [],
  viewStartDate: null,
  timezone: null,
  latestHosRecord: null,
  singleDayViewOnly: false,
};



// WEBPACK FOOTER //
// ./src/components/HoursOfService/Helper/HosGraphicalView/index_v2.jsx